import React, { useMemo, useRef } from 'react';
import { Dialog } from "../Dialog";
import styled from 'styled-components';
import { theme } from "../../theme";
import { Text } from "../Text";
import { Stack } from "../Stack";
import { Button } from "../Button";
import { noop } from "../../utils/noop";
import { IconCheckCircleFill } from "@frameio/vapor-icons/build/esm/icons/IconCheckCircleFill";
import { IconInfoFill } from "@frameio/vapor-icons/build/esm/icons/IconInfoFill";
import { IconWarningFill } from "@frameio/vapor-icons/build/esm/icons/IconWarningFill";
import { IconXCircleFill } from "@frameio/vapor-icons/build/esm/icons/IconXCircleFill";
export function AlertDialog(_ref) {
  var title = _ref.title,
      details = _ref.details,
      _ref$confirmText = _ref.confirmText,
      confirmText = _ref$confirmText === void 0 ? 'OK' : _ref$confirmText,
      _ref$onConfirm = _ref.onConfirm,
      onConfirm = _ref$onConfirm === void 0 ? noop : _ref$onConfirm,
      isShowing = _ref.isShowing,
      leastDestructiveRef = _ref.leastDestructiveRef,
      actions = _ref.actions,
      _ref$variant = _ref.variant,
      variant = _ref$variant === void 0 ? 'info' : _ref$variant,
      _ref$zIndex = _ref.zIndex,
      zIndex = _ref$zIndex === void 0 ? theme.zIndices.modal : _ref$zIndex;
  var defaultFocusRef = useRef();
  var renderedActions = useMemo(function () {
    if (actions) {
      return actions;
    }

    return /*#__PURE__*/React.createElement(AlertDialogButton, {
      ref: defaultFocusRef,
      color: "primary",
      onClick: onConfirm
    }, confirmText);
  }, [actions, confirmText, onConfirm]);
  return /*#__PURE__*/React.createElement(StyledDialog, {
    isShowing: isShowing,
    initialFocusRef: leastDestructiveRef !== null && leastDestructiveRef !== void 0 ? leastDestructiveRef : defaultFocusRef,
    enableClickOutsideToClose: false,
    role: "alertdialog",
    size: "x-small",
    zIndex: zIndex
  }, /*#__PURE__*/React.createElement(Stack, {
    alignX: "center",
    gap: 16
  }, variant !== 'neutral' && /*#__PURE__*/React.createElement(IconForVariant, {
    variant: variant
  }), /*#__PURE__*/React.createElement(Stack, {
    gap: 12
  }, /*#__PURE__*/React.createElement(AlertTitle, null, title), typeof details === 'string' ? /*#__PURE__*/React.createElement(AlertDialogDetails, null, details) : details), /*#__PURE__*/React.createElement(AlertDialogButtonGroup, null, renderedActions)));
}
var StyledDialog = styled(Dialog).withConfig({
  displayName: "AlertDialog__StyledDialog",
  componentId: "vapor__sc-11zyvdt-0"
})(["padding:", ";text-align:center;"], theme.spaces[16]);
export var AlertTitle = styled(Text).attrs({
  color: 'primary',
  variant: 'header'
}).withConfig({
  displayName: "AlertDialog__AlertTitle",
  componentId: "vapor__sc-11zyvdt-1"
})([""]);
export var AlertDialogDetails = styled(Text).attrs({
  color: 'secondary'
}).withConfig({
  displayName: "AlertDialog__AlertDialogDetails",
  componentId: "vapor__sc-11zyvdt-2"
})([""]);
export var AlertDialogButtonGroup = styled.div.withConfig({
  displayName: "AlertDialog__AlertDialogButtonGroup",
  componentId: "vapor__sc-11zyvdt-3"
})(["display:grid;grid-row-gap:", ";width:100%;min-width:0;", "{display:inline-grid;grid-column-gap:", ";grid-auto-columns:1fr;grid-auto-flow:column;}"], theme.spaces[8], theme.mediaQueries.tablet, theme.spaces[8]);
export var AlertDialogButton = styled(Button).attrs({
  block: true
}).withConfig({
  displayName: "AlertDialog__AlertDialogButton",
  componentId: "vapor__sc-11zyvdt-4"
})([""]);
var palette = theme.colors.palette;

function getAlertColors(color) {
  return {
    '--shadow': palette["".concat(color, "900")],
    '--background': palette["".concat(color, "900")],
    '--color': palette["".concat(color, "500")]
  };
}

function IconForVariant(_ref2) {
  var variant = _ref2.variant;

  switch (variant) {
    case 'success':
      {
        return /*#__PURE__*/React.createElement(IconShadow, {
          style: getAlertColors('alertSuccess')
        }, /*#__PURE__*/React.createElement(IconCheckCircleFill, null));
      }

    case 'error':
      return /*#__PURE__*/React.createElement(IconShadow, {
        style: getAlertColors('alertError')
      }, /*#__PURE__*/React.createElement(IconXCircleFill, null));

    case 'info':
      return /*#__PURE__*/React.createElement(IconShadow, {
        style: getAlertColors('skyBlue')
      }, /*#__PURE__*/React.createElement(IconInfoFill, null));

    case 'warning':
      return /*#__PURE__*/React.createElement(IconShadow, {
        style: getAlertColors('alertWarning')
      }, /*#__PURE__*/React.createElement(IconWarningFill, null));

    default:
      return null;
  }
}

var IconShadow = styled.div.withConfig({
  displayName: "AlertDialog__IconShadow",
  componentId: "vapor__sc-11zyvdt-5"
})(["display:flex;justify-content:center;align-items:center;width:49px;height:49px;box-shadow:inset 0 0 0 1px var(--shadow);background-color:var(--background);border-radius:50%;color:var(--color);"]);